import { Model } from '../../model/model';
import { FormatEngine as BaseFormatEngine, EM_ACTION, FIELD_KIND } from '@wizeoo/akm-compute-lib';
import { PivotFunction } from '../baselines/pivot-function.obj';
import { WIZ_COMMAND_STATE, WizNumberUtils } from '@wizeoo/akm-core-lib';
import { FormatVO } from '../../vo/workingdata/format.vo';
import { BusinessException, EntityModificationPack, Format } from '@wizeoo/akm-message-lib';
import { cloneDeep } from 'lodash-es';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';


/**
 * Manager des formats pour le moteur
 */
export class FormatEngine extends BaseFormatEngine  {

    constructor(model: Model) {
        super(model);
    }
    public get model(): Model {
        return this._model as Model;
    }


    public override newFormatVO(source?: Format): FormatVO {
        return new FormatVO(this._model as Model, source);
    }

    public override getFormatList(): FormatVO[] {
        return this._model.formatList ? this._model.formatList as FormatVO[] : null;
    }

    public override getFormatMap(): Map<string, Map<string, FormatVO>> {
        return this._model.formatMap ? this._model.formatMap as Map<string, Map<string, FormatVO>> : null;
    }

    /**
     * Récupération d'un format par son couple bu / code
     * @param buCode la bu de rattachement de ce style
     * @param code Le code du format
     */
    public getFormat(buCode: string, code: string): FormatVO {
        let svo: FormatVO = null;
        if (this.getFormatMap() && this.getFormatMap().get(buCode)) {
            svo = this.getFormatMap().get(buCode).get(code);
        }
        if(!!svo && !svo.partsBuilded) {
            svo.buildParts();
        }
        return svo;
    }


    /**
   * moteur d'application des formats
   * @param value au format string
   * @param dataKind type de donnée
   * @param format BU|formatCode
   * @param méthode d'aggrégat, pour override éventuel du format (count = entier)
   * @returns string formatté
   */
    public applyFormat(value: string, dataKind: string, format: string, wizAgregateFunc: string): string {
        let result: string = value;
    
        if (!!wizAgregateFunc) { // override si aggregat et type de retour
            const pf: PivotFunction = this.model.aggregateEngine.functionMap().get(wizAgregateFunc);
            if (!!pf.dataKind) {
            switch (dataKind) {
                case FIELD_KIND.INT:
                if (!!value) {  // On filtre aussi chaine vide ('') pour ne pas se retrouver avec un 0 en résultat du formatage
                    result = WizNumberUtils.formatInteger(Number(value));
                }
                break;
                case FIELD_KIND.NUMBER:
                case FIELD_KIND.DATE:
                case FIELD_KIND.STRING:
                break;
                case FIELD_KIND.BOOLEAN:
                  if(format) {
                    return this.format(value, dataKind, format);
                  }
                break;
            }
            if (result === undefined) {
                result = '';
            }
            return result;
            }
        }
        // gestion des cas sans aggrégat
        // Appel au formatage contenu dans la classe de base
        return this.format(value, dataKind, format);
    }  

  /**
   * Clone pour édition
   * @param sty  à cloner
   * @return FormatVO,  Un clone
   */
    public clone(sty: FormatVO): FormatVO {
        const clone = new FormatVO(this.model, cloneDeep(sty.source));
        return clone;
    }

  /** création d'un nouveau style */
  public buildFake(): FormatVO {
    const result: FormatVO = new FormatVO(this.model);
    result.domain = this.model.domainCode;
    result.businessUnitCode = this.model.businessUnitCode;
    return result;
  }

  /**
   * create/ update d'un format
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: FormatVO, from: FormatVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.entries.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(); // retour en vue
    }
  }

  /**
   * delete
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: FormatVO, from: FormatVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: FormatVO = cmd.sourceVO[0] as FormatVO;

      // une seule modification à la fois, packEntry donne l'objet
      const format: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = format; // le clone mis à jour
            this.getFormat(vo.businessUnitCode, vo.code).source = format;
            this.getFormat(vo.businessUnitCode, vo.code).partsBuilded = false;
            break;
          case EM_ACTION.CREATE:
            vo.source = format;
            this.addFormat(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = format;
            this.removeFormat(vo.businessUnitCode, vo.code);
            break;
        }
      }
      cmd.viewCallBack(vo, operation); // retour en vue
    }
  }


}
